import type {PulledBottom_AllWhereQuery} from '@/graphql/generated/graphql.ts';
import type {PulledBottom} from '@/types/well';

type PulledBottomRow = NonNullable<NonNullable<PulledBottom_AllWhereQuery['pulledBottom_AllWhere']>[number]>;

// pulledDate comes back typed `unknown` (DateOnly has no scalar mapping
// in codegen.yml, same as every other custom scalar here) — `unknown ??
// ''` itself types as `{}`, not `string`, so this converts first rather
// than coalescing directly. Same idiom as lib/barrelTestMapping.ts's
// testDateOf.
function pulledDateOf(row: PulledBottomRow): string {
   return row.pulledDate ? String(row.pulledDate) : '';
}

// bblsPulled = (Gauge Before − Gauge After) × the selected tank's
// existing `multiplier` field (bbl/inch conversion factor) — reusing
// Tank.multiplier rather than adding a new field (Duane, 2026-09-29:
// "I assume it would be the same as the BarrelTest" — see this
// function's sibling computeEstOilInBottoms for the other half of that
// same assumption). Rounds to 2 decimal places, matching BarrelTest's own
// oilBbls rounding (BarrelTestFormPanel's oilBblsPreview).
export function computeBblsPulled(gaugeBeforeInches: number, gaugeAfterInches: number, tankMultiplier: number): number {
   return Math.round((gaugeBeforeInches - gaugeAfterInches) * tankMultiplier * 100) / 100;
}

// estOilInBottoms = bblsPulled × (1 − BS&W% ÷ 100) — treats BS&W% as the
// non-oil (water + sediment) share, same convention as BarrelTest's own
// Oil %/Water % split (BarrelTestFormPanel). UNCONFIRMED with the client
// as of 2026-09-29 — Duane's own call: "I assume it would be the same as
// the BarrelTest. Otherwise they would have called out the difference.
// Use that for now... bring up for me to confirm with the client" (he's
// checking this at his Oct 2 scrum/accomplishments review). If the
// client's mockup actually shows a different formula, only this function
// (and its BarrelTest-style helper text in PulledBottomFormPanel) needs
// to change.
export function computeEstOilInBottoms(bblsPulled: number, bswPercent: number): number {
   return Math.round(bblsPulled * (1 - bswPercent / 100) * 100) / 100;
}

/**
 * Maps real pulledBottom_AllWhere rows to the PulledBottom domain shape,
 * denormalizing leaseName/tankNumber in the same way lib/wellMapping.ts/
 * lib/barrelTestMapping.ts denormalize wellName.
 */
export function toPulledBottoms(
   rows: (PulledBottomRow | null)[] | null | undefined,
   leaseNameById: Map<string, string>,
   tankNumberById: Map<string, string>,
): PulledBottom[] {
   return (rows ?? [])
      .filter((row): row is PulledBottomRow => row != null)
      .map((row) => {
         const leaseId = row.leaseId != null ? String(row.leaseId) : '';
         const tankId = row.tankId != null ? String(row.tankId) : '';
         return {
            id: String(row.id),
            leaseId,
            leaseName: leaseNameById.get(leaseId) ?? 'Unknown lease',
            tankId,
            tankNumber: tankNumberById.get(tankId) ?? 'Unknown tank',
            pulledDate: pulledDateOf(row),
            bswPercent: row.bswPercent ?? null,
            gaugeBeforeInches: row.gaugeBeforeInches ?? null,
            gaugeAfterInches: row.gaugeAfterInches ?? null,
            bblsPulled: row.bblsPulled ?? 0,
            estOilInBottoms: row.estOilInBottoms ?? null,
            createdByUserId: row.createdByUserId != null ? String(row.createdByUserId) : '',
            createdDateTime: row.createdDateTime != null ? String(row.createdDateTime) : '',
            updatedByUserId: row.updatedByUserId != null ? String(row.updatedByUserId) : '',
            updatedDateTime: row.updatedDateTime != null ? String(row.updatedDateTime) : '',
         };
      });
}
